import isFeatureEnabled from '~/components/common/is-feature-enabled';
import isAddonEnabled from '~/is-addon-enabled';
import { gradebookBaseUrl } from '~/clc/features/academy-gradebook/utils';
import { APP_CONSTANTS } from '~/common/explicit-exports';

const Permission = APP_CONSTANTS.PERMISSIONS;

const academiesEnabled = isAddonEnabled('academyAddonEnabled');
const communityEnabled = isAddonEnabled('communityAddonEnabled');

export function CustomerLearningCloudAppOptions(accId: string, clcHubId: string) {
  return {
    ACADEMIES: {
      id: 3,
      shouldShow: () => academiesEnabled || isFeatureEnabled('academy_upsell_page'),
      title: 'Academy',
      href: `/admin/academies/${accId}/content`,
      src: '/images/vertnav_academy_icon.svg',
      icon: 'academy',
      iconName: 'academy:regular',
      sortOrder: 1,
      newBadge: false,
      allowedPermissions: [Permission.ADMIN, Permission.ACADEMY_ADMIN],
      submenus: {
        CONTENT: {
          id: 0,
          title: 'Content',
          allowedPermissions: [Permission.ADMIN, Permission.ACADEMY_ADMIN],
          route: /admin\/academies\/.+\/content(?!-metadata)\/?$/,
          hrefFnc: (academyId) => `/admin/academies/${academyId}/content`,
        },
        USERS: {
          id: 1,
          shouldShow: () => isFeatureEnabled('academy_user_management'),
          title: 'Enrollments',
          hrefFnc: !isFeatureEnabled('academies_assignments')
            ? (academyId) => `/admin/academies/${academyId}/users`
            : null,
          allowedPermissions: [Permission.ADMIN, Permission.ACADEMY_ADMIN],
          route: /admin\/academies\/.+\/(trainings|certification_enrollments|path_enrollments)/,
          options: [
            {
              title: 'Trainings',
              hrefFnc: (academyId) => `/admin/academies/${academyId}/trainings`,
              shouldShow: () => isFeatureEnabled('academies_assignments'),
            },
            {
              title: 'Certifications',
              hrefFnc: (academyId) => `/admin/academies/${academyId}/certification_enrollments`,
              shouldShow: () => isFeatureEnabled('academies_assignments'),
            },
            {
              title: 'Paths',
              hrefFnc: (academyId) => `/admin/academies/${academyId}/path_enrollments`,
              shouldShow: () => isFeatureEnabled('academies_assignments') && isFeatureEnabled('academy_paths'),
            },
            {
              title: 'Gradebook',
              hrefFnc: (academyId) => gradebookBaseUrl(academyId),
              shouldShow: () => isFeatureEnabled('fy26_q3_academy_gradebook'),
            },
          ],
        },
        ANALYTICS: {
          id: 2,
          title: 'Reporting',
          route: /admin\/academies\/.+\/(analytics|saved_reports|training_analytics)/,
          allowedPermissions: [Permission.ADMIN, Permission.ACADEMY_ADMIN],
          options: [
            {
              title: 'Dashboard',
              hrefFnc: (academyId) => `/admin/academies/${academyId}/analytics`,
              shouldShow: () => !isFeatureEnabled('fy26-q2-deprecate-academy-reporting-dashboard-page'),
            },
            {
              title: 'Trainings',
              hrefFnc: (academyId) => `/admin/academies/${academyId}/training_analytics`,
              shouldShow: () => !isFeatureEnabled('academies_saved_reports'),
            },
            {
              title: 'Report Builder',
              hrefFnc: (academyId) => `/admin/academies/${academyId}/saved_reports`,
              shouldShow: () => isFeatureEnabled('academies_saved_reports'),
            },
            {
              title: 'Contacts',
              hrefFnc: (academyId) => `/admin/academies/${academyId}/analytics/users`,
              shouldShow: () => !isFeatureEnabled('fy26-q2-deprecate-academy-reporting-contacts-page'),
            },
            {
              title: 'Insights',
              hrefFnc: (academyId) => `/admin/academies/${academyId}/analytics/data-viz`,
              shouldShow: () =>
                isFeatureEnabled('data_viz_external') &&
                !isFeatureEnabled('fy26-q2-deprecate-academy-reporting-insights-page'),
            },
            {
              title: 'Analytics Studio',
              betaBadge: true,
              hrefFnc: (academyId) => `/admin/academies/${academyId}/analytics/dashboards`,
              shouldShow: () => isFeatureEnabled('fy25_q1_embedded_analytics'),
            },
          ],
        },
        THEMING: {
          id: 3,
          title: 'Theming',
          route: /admin\/academies\/.+\/(theming)/,
          allowedPermissions: [Permission.ADMIN, Permission.ACADEMY_ADMIN],
          options: [
            {
              title: 'Theme Editor',
              hrefFnc: (academyId: string) => `/admin/academies/${academyId}/theming`,
            },
            {
              title: 'Text Overrides',
              hrefFnc: (academyId: string) => `/admin/academies/${academyId}/theming/text_overrides`,
            },
          ],
        },
        PAGES: {
          id: 4,
          title: 'Pages',
          route: /admin\/academies\/.+\/(pages|catalog)/,
          allowedPermissions: [Permission.ADMIN, Permission.ACADEMY_ADMIN],
          options: [
            {
              title: 'Custom Pages',
              hrefFnc: (academyId: string) => `/admin/academies/${academyId}/pages`,
              shouldShow: () => isFeatureEnabled('clc-academy-custom-pages'),
              allowedPermissions: [Permission.ADMIN, Permission.ACADEMY_ADMIN],
            },
            {
              title: 'Catalog',
              hrefFnc: (academyId: string) => `/admin/academies/${academyId}/catalog`,
            },
          ],
        },
        INTEGRATIONS: {
          id: 5,
          title: 'Integrations',
          route: /admin\/academies\/.+\/integrations/,
          hrefFnc: (academyId) => `/admin/academies/${academyId}/integrations`,
          allowedPermissions: [Permission.ADMIN, Permission.ACADEMY_ADMIN],
        },
        SETTINGS: {
          id: 6,
          title: 'Settings',
          allowedPermissions: [Permission.ADMIN, Permission.ACADEMY_ADMIN],
          route: /admin\/academies\/.+\/settings/,
          options: [
            {
              title: 'General',
              hrefFnc: (academyId) => `/admin/academies/${academyId}/settings/general`,
            },
            {
              title: 'Emails',
              hrefFnc: (academyId) => `/admin/academies/${academyId}/settings/emails`,
            },
            {
              title: 'Certifications',
              hrefFnc: (academyId) => `/admin/academies/${academyId}/settings/certifications`,
            },
            {
              title: 'Content Metadata',
              hrefFnc: (academyId) => `/admin/academies/${academyId}/settings/content-metadata`,
            },
            {
              title: 'Ecommerce',
              hrefFnc: (academyId) => `/admin/academies/${academyId}/settings/ecommerce`,
              shouldShow: () => isFeatureEnabled('ecommerce'),
            },
            {
              title: 'AI Settings',
              hrefFnc: (academyId) => `/admin/academies/${academyId}/settings/ai`,
              shouldShow: () => isFeatureEnabled('fy26_q2_clc_learner_personalization'),
            },
            {
              title: 'Recommendations',
              hrefFnc: (academyId) => `/admin/academies/${academyId}/settings/recommendations`,
              shouldShow: () => isFeatureEnabled('fy26_q2_clc_recommendations'),
            },
          ],
        },
        ECOMMERCE: {
          id: 7,
          title: 'Ecommerce',
          allowedPermissions: [Permission.ADMIN, Permission.ACADEMY_ADMIN],
          route: /admin\/academies\/.+\/ecommerce/,
          hrefFnc: !isFeatureEnabled('ecommerce_promotions')
            ? (academyId) => `/admin/academies/${academyId}/ecommerce/purchases`
            : null,
          options: [
            {
              title: 'Purchases & Refunds',
              hrefFnc: (academyId) => `/admin/academies/${academyId}/ecommerce/purchases`,
              shouldShow: () => isFeatureEnabled('ecommerce_promotions'),
            },
            {
              title: 'Promotions',
              hrefFnc: (academyId) => `/admin/academies/${academyId}/ecommerce/promos`,
              shouldShow: () => isFeatureEnabled('ecommerce_promotions'),
            },
          ],
        },
      },
    },
    COMMUNITY: {
      elementId: 'intercom-clc-community',
      id: 8,
      shouldShow: () => isFeatureEnabled('fy24_q3_clc_community'),
      title: 'Community',
      href: `/admin/customer_learning_cloud_hub/${clcHubId}/community`,
      src: '/images/vertnav_academy_icon.svg',
      icon: 'team',
      iconName: 'company:regular',
      sortOrder: 2,
      newBadge: !communityEnabled,
      allowedPermissions: [Permission.ADMIN, Permission.ACADEMY_ADMIN],
      submenus: null,
    },
    CONTACTS: {
      elementId: 'intercom-clc-nav-contacts',
      id: 6,
      shouldShow: () => academiesEnabled || communityEnabled || isFeatureEnabled('academy_upsell_page'),
      title: 'Contacts',
      href: `/admin/customer_learning_cloud_hub/${clcHubId}/contacts/contacts`,
      src: '/images/vertnav_academy_icon.svg',
      icon: 'team',
      iconName: 'team:regular',
      sortOrder: 3,
      newBadge: false,
      allowedPermissions: [Permission.ADMIN, Permission.ACADEMY_ADMIN],
      submenus: null,
    },
    SETTINGS: {
      elementId: 'intercom-clc-nav-settings',
      id: 7,
      shouldShow: () => academiesEnabled || communityEnabled || isFeatureEnabled('academy_upsell_page'),
      title: 'Settings',
      href: `/admin/customer_learning_cloud_hub/${clcHubId}/settings/general`,
      src: '/images/vertnav_academy_icon.svg',
      icon: 'cog',
      iconName: 'controls-cog:regular',
      sortOrder: 4,
      newBadge: false,
      allowedPermissions: [Permission.ADMIN, Permission.ACADEMY_ADMIN],
      submenus: null,
    },
  };
}
